import {useEffect} from 'react';
import {DialogStatus} from '../../Enums';
import {UserInterfaceActions} from '../../Components/Context/UserInterface/UserInterfaceActionTypes';
import {AssetWorkflow} from '@taopix/taopix-asset-manager';
import {useSelector} from 'react-redux';
import {UserAssetSelector} from '../../Selectors';
import {useUiState} from '../../Components/Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../../Components/Experience/ExperienceProvider';
import {useUserFlowState} from '../../Components/Context/UserFlow/UserFlowContext';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import {useTaopix} from '../../Components/Context/TaopixContext';
import {AssistantType} from '@taopix/taopix-core';

const useAIObserver = (workflow?: AssetWorkflow) => {
  const taopix = useTaopix();
  const ui = useUiState();
  const userFlow = useUserFlowState();
  const {experience} = useExperience();
  const assets = useSelector(UserAssetSelector);
  const ignoredThreshold = 50;

  const isPreviewStepActive = userFlow.isStepActive(
    UserFlowType.Assistant,
    UserFlowStep.AssistantPreview
  );
  const isSmartDesign =
    taopix.state.loaded &&
    AssistantType.SmartDesign === experience?.WIZARD?.designAssistantType?.designAssistantType;

  /**
   * Calculates if the dialog should be shown based on how many pictures do not have metadata.
   */
  const showSomePicturesIgnoredDialog = () => {
    if (assets.length > 0) {
      const showMsgStats = assets.reduce(
        (accumulator, asset) => {
          if (isNaN(Date.parse(asset.metaData?.dateTimeOriginal))) {
            accumulator.count++;
            accumulator.percentage = Math.ceil((accumulator.count / assets.length) * 100);
          }

          return accumulator;
        },
        {count: 0, percentage: 0}
      );

      if (
        showMsgStats.percentage >= ignoredThreshold &&
        DialogStatus.Seen !== ui.state.dialogs.picturesIgnoredBySmartDesignNotification
      ) {
        ui.dispatch(
          UserInterfaceActions.setDialogStatus({
            picturesIgnoredBySmartDesignNotification: DialogStatus.Open,
          })
        );
      }
    } else if (
      0 === assets.length &&
      DialogStatus.Seen === ui.state.dialogs.picturesIgnoredBySmartDesignNotification
    ) {
      // Reset if all assets removed.
      ui.dispatch(
        UserInterfaceActions.setDialogStatus({
          picturesIgnoredBySmartDesignNotification: DialogStatus.Closed,
        })
      );
    }
  };

  // Show on Assistant preview step.
  useEffect(() => {
    if (!taopix.state.loaded) return;
    if (!isSmartDesign) return;
    if (ui.state.busy) return;

    if (isPreviewStepActive) {
      showSomePicturesIgnoredDialog();
    }
  }, [taopix.state.loaded, ui.state.busy, isSmartDesign, isPreviewStepActive]);

  // Show in Editor after switch if preview step is off.
  useEffect(() => {
    if (!taopix.state.loaded) return;
    if (!isSmartDesign) return;
    if (ui.state.busy) return;

    if (
      !experience.WIZARD.previewStep.showPreviewStep &&
      ui.state.fromAssistant.fromAssistant &&
      ui.state.fromAssistant.assistantRan
    ) {
      showSomePicturesIgnoredDialog();
    }
  }, [
    taopix.state.loaded,
    ui.state.busy,
    isSmartDesign,
    ui.state.fromAssistant.fromAssistant,
    ui.state.fromAssistant.assistantRan,
  ]);

  return;
};

export default useAIObserver;
